import { ContextHolder, useAuthActions } from '@frontegg/react';
import { api } from 'src/services';
import { useState } from 'react';
import { useEmbeddedLogin } from './useEmbeddedLogin';
import {
  getEncounterByEhrAppointmentId,
  updateEncounterByEhrId,
  syncEncounter,
} from 'src/hooks/encounter';
import { syncEncounterPractitioner } from 'src/hooks/practitioner';

// Session storage helpers for window-isolated auth state
const setSessionItem = (key: string, value: string) => {
  sessionStorage.setItem(key, value);
};

const getSessionItem = (key: string): string | null => {
  return sessionStorage.getItem(key);
};

const removeSessionItem = (key: string) => {
  sessionStorage.removeItem(key);
};

const getEmbeddedParams = () => {
  const queryParams = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams(window.location.hash.substring(1));

  const urlParams = {
    fhirPatientId: queryParams.get('fhirPatientId'),
    fhirEncounterId: queryParams.get('fhirEncounterId'),
    fhirAppointmentId: queryParams.get('fhirAppointmentId'),
    token: hashParams.get('token'),
  };

  // When a new token is provided (new SSO login)
  if (urlParams.token) {
    // If encounter context is provided, store in sessionStorage
    if (urlParams.fhirEncounterId) {
      setSessionItem('embedded_fhirEncounterId', urlParams.fhirEncounterId);
      if (urlParams.fhirPatientId) {
        setSessionItem('embedded_fhirPatientId', urlParams.fhirPatientId);
      }
      if (urlParams.fhirAppointmentId) {
        setSessionItem(
          'embedded_fhirAppointmentId',
          urlParams.fhirAppointmentId
        );
      }
    } else {
      // If logging in without encounter context, clear the encounter storage
      // This handles the case where user was logged in with encounter context
      // and then logs in again without it (e.g., from toolbar vs visit note)
      removeSessionItem('embedded_fhirEncounterId');
      removeSessionItem('embedded_fhirPatientId');
      removeSessionItem('embedded_fhirAppointmentId');
      // Clear any stored selectedEncounter from sessionStorage
      Object.keys(sessionStorage)
        .filter((key) => key.endsWith('_selectedEncounter'))
        .forEach((key) => sessionStorage.removeItem(key));
    }
  }

  return {
    fhirPatientId:
      urlParams.fhirPatientId || getSessionItem('embedded_fhirPatientId'),
    fhirEncounterId:
      urlParams.fhirEncounterId || getSessionItem('embedded_fhirEncounterId'),
    fhirAppointmentId:
      urlParams.fhirAppointmentId ||
      getSessionItem('embedded_fhirAppointmentId'),
    token: urlParams.token,
  };
};

const getStoredAuth = () => ({
  accessToken: getSessionItem('embedded_access_token'),
  user: (() => {
    const userStorage = getSessionItem('embedded_user');
    if (!userStorage) return null;
    try {
      return JSON.parse(userStorage);
    } catch (error) {
      console.log('error parsing user from sessionStorage', error);
      return null;
    }
  })(),
});

const setStoredAuth = (
  accessToken: string,
  user: any,
  fhirEncounterId: string | null
) => {
  setSessionItem('embedded_access_token', accessToken);
  setSessionItem('embedded_user', JSON.stringify(user));
  if (fhirEncounterId) {
    setSessionItem('embedded_lastFhirEncounterId', fhirEncounterId);
  }
};

const clearStoredAuth = () => {
  [
    'embedded_fhirPatientId',
    'embedded_fhirEncounterId',
    'embedded_fhirAppointmentId',
    'embedded_access_token',
    'embedded_lastFhirEncounterId',
    'embedded_user',
  ].forEach(removeSessionItem);
};

export const isEmbeddedEnvironment = () => {
  const { token } = getEmbeddedParams();
  const hasStoredEmbeddedAuth = !!getSessionItem('embedded_access_token');
  return !!token || hasStoredEmbeddedAuth;
};

export const getEmbeddedAccessToken = () =>
  getSessionItem('embedded_access_token');

export const getEmbeddedUser = () => {
  try {
    const userStorage = getSessionItem('embedded_user');
    return userStorage ? JSON.parse(userStorage) : null;
  } catch {
    return null;
  }
};

export const getEmbeddedEncounterId = () =>
  getSessionItem('embedded_fhirEncounterId');

export const clearEmbeddedEncounterId = () => {
  removeSessionItem('embedded_fhirEncounterId');
};

export const clearEmbeddedAuthCookies = clearStoredAuth;

/**
 * Hook to check if the app is running in embedded mode with an encounter ID.
 * Use this to pre-filter encounters and disable other filters.
 */
export const useEmbeddedEncounter = () => {
  const isEmbedded = isEmbeddedEnvironment();
  const embeddedEncounterId = getEmbeddedEncounterId();
  const isEmbeddedWithEncounter = isEmbedded && !!embeddedEncounterId;

  return {
    isEmbedded,
    embeddedEncounterId,
    isEmbeddedWithEncounter,
  };
};

export const generateAccessToken = async () => {
  const { token, fhirPatientId, fhirEncounterId } = getEmbeddedParams();

  if (!token) {
    throw new Error('OP token not available');
  }

  const result = await api.post('/v3/embedded/login', {
    token,
    fhirPatientId,
    fhirEncounterId,
  });
  return result?.data;
};

type SyncEncounterParams = {
  orgUuid: string;
  fhirPatientId: string | null;
  fhirEncounterId: string | null;
  fhirAppointmentId: string | null;
};

const handleEncounterSync = async ({
  orgUuid,
  fhirPatientId,
  fhirEncounterId,
  fhirAppointmentId,
}: SyncEncounterParams) => {
  if (!fhirPatientId || !fhirEncounterId) return;

  // Practitioner sync is non-critical; log and continue on failure
  try {
    await syncEncounterPractitioner({ org_uuid: orgUuid, fhirEncounterId });
  } catch {
    console.error(
      'Failed to sync practitioner, continuing with encounter sync'
    );
  }

  if (fhirAppointmentId) {
    try {
      const encounter = await getEncounterByEhrAppointmentId(fhirAppointmentId);
      if (encounter?.encounter_id && encounter?.appointment_id) {
        await updateEncounterByEhrId(encounter.appointment_id, {
          ehr_id: fhirEncounterId,
        });
      }
      return;
    } catch (error: any) {
      if (error?.response?.status !== 404) {
        throw error;
      }
      // 404 falls through to syncEncounter below
    }
  }

  await syncEncounter({
    org_uuid: orgUuid,
    fhirPatientId,
    fhirEncounterId,
    ...(fhirAppointmentId && { fhirAppointmentId }),
  });
};

export type EmbeddedAuthError = {
  title: string;
  message: string;
} | null;

export const useEmbeddedAuthActions = () => {
  const { setAuthState } = useAuthActions();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<EmbeddedAuthError>(null);

  const embeddedLogin = async () => {
    setLoading(true);
    setError(null);

    try {
      const { accessToken, user } = getStoredAuth();
      const { token, fhirAppointmentId, fhirPatientId, fhirEncounterId } =
        getEmbeddedParams();

      const storedEncounterId = getSessionItem('embedded_lastFhirEncounterId');
      const encounterChanged =
        fhirEncounterId &&
        storedEncounterId &&
        fhirEncounterId !== storedEncounterId;

      if (accessToken && user && !encounterChanged) {
        ContextHolder.default().setAccessToken(accessToken);
        setAuthState({ isAuthenticated: true, user: user });
        return;
      }

      // Clear stored auth if encounter changed
      if (encounterChanged) {
        clearStoredAuth();
      }
      if (!token) {
        clearStoredAuth();
        return;
      }

      let result;
      try {
        result = await generateAccessToken();
      } catch (tokenError) {
        console.error('Error generating access token:', tokenError);
        setError({
          title: 'SSO login failed',
          message:
            'Please relaunch from your EHR or contact support if the issue persists.',
        });
        return;
      }

      if (result?.access_token && result?.user) {
        setStoredAuth(result.access_token, result.user, fhirEncounterId);
        ContextHolder.default().setAccessToken(result.access_token);
        setAuthState({ isAuthenticated: true, user: result.user });

        try {
          await handleEncounterSync({
            orgUuid: result.user.tenantId,
            fhirPatientId,
            fhirEncounterId,
            fhirAppointmentId,
          });
        } catch (encounterError: any) {
          console.error('Error syncing encounter:', encounterError);
          const status = encounterError?.response?.status;
          if (status === 404) {
            setError({
              title: 'Encounter not found',
              message:
                'Please verify the encounter exists in your EHR and try again.',
            });
          } else {
            setError({
              title: 'Encounter sync failed',
              message:
                'Something went wrong while loading the encounter. Please try relaunching from your EHR or contact support if the issue persists.',
            });
          }
        }
      }
    } catch (error) {
      console.error('Embedded login error:', error);
      setError({
        title: 'SSO login failed - Internal error',
        message:
          'Please relaunch from your EHR or contact support if the issue persists.',
      });
    } finally {
      setLoading(false);
    }
  };

  const embeddedLogout = () => {
    clearStoredAuth();
    setAuthState({ isAuthenticated: false, user: null });
  };

  return { embeddedLogin, embeddedLogout, loading, error };
};

export const isVimOAuthFlow = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const code = urlParams.get('code');
  const state = urlParams.get('state');
  const pathname = window.location.pathname;
  return pathname.includes('/ehr/vim/launch') && !!code && !!state;
};

export const useVimAuthActions = () => {
  const { setAuthState, loadEntitlements } = useAuthActions();
  const { embeddedLogin } = useEmbeddedLogin();
  const [loading, setLoading] = useState(false);
  const [hasAttempted, setHasAttempted] = useState(false);

  const vimLogin = async () => {
    if (loading || hasAttempted) {
      return;
    }

    setLoading(true);
    setHasAttempted(true);

    try {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const state = urlParams.get('state');

      if (!code || !state) {
        setHasAttempted(false);
        return;
      }

      const loginResponse = await embeddedLogin({ token: code });
      console.log('loginResponse', loginResponse);

      if (loginResponse?.access_token && loginResponse?.user) {
        const { access_token, user } = loginResponse;

        ContextHolder.default().setAccessToken(access_token);
        setAuthState({ isAuthenticated: true, user: user as any });

        setSessionItem('embedded_access_token', access_token);
        setSessionItem('embedded_user', JSON.stringify(user));
        setSessionItem('frontegg_user', JSON.stringify(user));

        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );

        loadEntitlements();
      }
    } catch (error) {
      console.error('Vim authentication failed:', error);
      setHasAttempted(false);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return { vimLogin, loading };
};
